import { CompactPagination } from '@telegram-apps/telegram-ui'
import { useEffect, useRef, useState } from 'react'
import { useTranslate } from 'shared/lib/i18n'

/**
 * Swipeable photo gallery: native CSS scroll-snap (no carousel lib — momentum
 * physics come from the webview) with TGUI CompactPagination dots overlaid.
 */
export function LotImageSlider({
	photoUrls,
	alt,
	imgClassName = 'aspect-[4/5]',
}: {
	photoUrls: string[]
	alt: string
	/** Tailwind aspect/extra classes for each image (default marketplace frame 4:5). */
	imgClassName?: string
}) {
	const { t } = useTranslate()
	const scrollerRef = useRef<HTMLDivElement>(null)
	const [active, setActive] = useState(0)

	// Reset only when the actual photo set changes — a refetch returns a new
	// array identity with the same content and must not yank the dots back.
	const photosKey = photoUrls.join('\n')
	useEffect(() => {
		setActive(0)
		scrollerRef.current?.scrollTo({ left: 0 })
	}, [photosKey])

	const safeActive = Math.min(active, photoUrls.length - 1)

	function handleScroll() {
		const el = scrollerRef.current
		if (!el || el.clientWidth === 0) return
		const index = Math.round(el.scrollLeft / el.clientWidth)
		setActive(Math.max(0, Math.min(index, photoUrls.length - 1)))
	}

	return (
		<div className='relative'>
			<div
				ref={scrollerRef}
				onScroll={handleScroll}
				className='flex snap-x snap-mandatory overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
			>
				{photoUrls.map((url, i) => (
					<img
						key={url}
						src={url}
						alt={i === 0 ? alt : t('common.photoAlt', { title: alt, index: String(i + 1) })}
						loading='lazy'
						draggable={false}
						className={`w-full flex-none snap-center bg-[var(--tg-theme-bg-color,#212121)] object-contain ${imgClassName}`}
					/>
				))}
			</div>
			{photoUrls.length > 1 && (
				<CompactPagination
					mode='ambient'
					className='absolute bottom-2.5 left-1/2 -translate-x-1/2'
				>
					{photoUrls.map((url, i) => (
						<CompactPagination.Item
							key={url}
							selected={i === safeActive}
							onClick={(e) => {
								// Dots live inside clickable feed cards — don't navigate.
								e.stopPropagation()
								scrollerRef.current?.scrollTo({
									left: i * (scrollerRef.current?.clientWidth ?? 0),
									behavior: 'smooth',
								})
							}}
						>
							{t('common.photo', { index: String(i + 1) })}
						</CompactPagination.Item>
					))}
				</CompactPagination>
			)}
		</div>
	)
}
